import { DATA_STYLE,HEADER_STYLE, SUBHEADER_STYLE } from "../excelReportStyles";
import {
  addBordersToSheet,
  formatComparisonValue,
  formatPlantTrainName,
  parseJSON,
} from "../excelReportUtils";
import { formatUnitForDisplay } from "../../../../utils/Utils";

export const createBenchmarkComparisonWithThresholdSheet = (
  sheet,
  trainsData,
  t,
) => {
  if (!Array.isArray(trainsData) || trainsData.length === 0) {
    return;
  }

  const pickByAliases = (source, aliases, fallback = null) => {
    for (const alias of aliases) {
      const value = source?.[alias];
      if (value !== null && value !== undefined && value !== "") {
        return value;
      }
    }
    return fallback;
  };

  const pressureUnit =
    formatUnitForDisplay(
      trainsData.find((train) => train?.units?.pressure)?.units?.pressure,
    ) || "-";
  const flowUnit =
    formatUnitForDisplay(
      trainsData.find((train) => train?.units?.flow)?.units?.flow,
    ) || "-";
  const permeateQualityUnit =
    formatUnitForDisplay(
      trainsData.find((train) => train?.units?.permeate_quality)?.units
        ?.permeate_quality,
    ) || "-";

  const comparisonDataByTrain = trainsData.map((train) => {
    const caPredictions = parseJSON(train.ca_predictions);
    return {
      predictedMinMax: caPredictions?.predicted_min_max || {},
      optimizedMinMax: caPredictions?.optimized_min_max || {},
      failureThresholds: caPredictions?.failure_thresholds || {},
    };
  });

  const metricsConfig = [
    {
      rowLabel: `${t("predictionStrings.feedPressure")} (${pressureUnit})`,
      aliases: ["feed_pressure"],
      thresholdAliases: ["max_feed_pressure", "feed_pressure"],
      thresholdType: "max",
    },
    {
      rowLabel: `${t("predictionStrings.pressureDrop")} (${pressureUnit})`,
      aliases: ["dp_raw", "pressure_drop"],
      thresholdAliases: ["max_pressure_drop", "pressure_drop", "dp_raw"],
      thresholdType: "max",
    },
    {
      rowLabel: `${t("predictionStrings.permeateFlow")} (${flowUnit})`,
      aliases: ["permeate_flowrate", "permeate_flow"],
      thresholdAliases: ["min_permeate_flow", "permeate_flowrate"],
      thresholdType: "min",
    },
    {
      rowLabel: `${t("predictionStrings.permeateConductivity")} (${permeateQualityUnit})`,
      aliases: ["permeate_quality"],
      thresholdAliases: ["max_permeate_quality", "permeate_quality"],
      thresholdType: "max",
    },
  ];

  const headerRow1 = sheet.getRow(1);
  headerRow1.getCell(1).value = t("benchMark.headers.metric");
  Object.assign(headerRow1.getCell(1), HEADER_STYLE);
  sheet.mergeCells(1, 1, 3, 1);

  let colIndex = 2;
  const trainColMap = {};
  trainsData.forEach((train, trainIdx) => {
    trainColMap[trainIdx] = colIndex;
    const trainNameCell = headerRow1.getCell(colIndex);
    trainNameCell.value = formatPlantTrainName(
      train.plant_name,
      train.plant_id,
      train.train_num,
    );
    Object.assign(trainNameCell, HEADER_STYLE);
    sheet.mergeCells(1, colIndex, 1, colIndex + 4);
    colIndex += 5;
  });
  headerRow1.commit();

  const headerRow2 = sheet.getRow(2);
  headerRow2.getCell(1).value = "";
  Object.assign(headerRow2.getCell(1), SUBHEADER_STYLE);

  const headerRow3 = sheet.getRow(3);
  headerRow3.getCell(1).value = "";
  Object.assign(headerRow3.getCell(1), SUBHEADER_STYLE);

  colIndex = 2;
  trainsData.forEach(() => {
    [
      [0, t("predictionStrings.predictedCipSchedule")],
      [2, t("predictionStrings.recommendedCipSchedule")],
    ].forEach(([offset, label]) => {
      const groupCell = headerRow2.getCell(colIndex + offset);
      groupCell.value = label;
      Object.assign(groupCell, SUBHEADER_STYLE);
      sheet.mergeCells(2, colIndex + offset, 2, colIndex + offset + 1);
    });

    const lifetimeHeaderCell = headerRow2.getCell(colIndex + 4);
    lifetimeHeaderCell.value = t("predictionStrings.lifetimeThreshold");
    Object.assign(lifetimeHeaderCell, SUBHEADER_STYLE);
    sheet.mergeCells(2, colIndex + 4, 3, colIndex + 4);

    [
      [0, t("predictionStrings.minShort")],
      [1, t("predictionStrings.maxShort")],
      [2, t("predictionStrings.minShort")],
      [3, t("predictionStrings.maxShort")],
    ].forEach(([offset, label]) => {
      const cell = headerRow3.getCell(colIndex + offset);
      cell.value = label;
      Object.assign(cell, SUBHEADER_STYLE);
    });

    colIndex += 5;
  });
  headerRow2.commit();
  headerRow3.commit();

  metricsConfig.forEach((metric, metricIdx) => {
    const rowNum = 4 + metricIdx;
    const row = sheet.getRow(rowNum);

    row.getCell(1).value = metric.rowLabel;
    Object.assign(row.getCell(1), DATA_STYLE);

    trainsData.forEach((_, trainIdx) => {
      const startCol = trainColMap[trainIdx];
      const { predictedMinMax, optimizedMinMax, failureThresholds } =
        comparisonDataByTrain[trainIdx];

      const predictedMetric = pickByAliases(
        predictedMinMax,
        metric.aliases,
        {},
      );
      const optimizedMetric = pickByAliases(
        optimizedMinMax,
        metric.aliases,
        {},
      );
      const thresholdRawValue = pickByAliases(
        failureThresholds,
        metric.thresholdAliases,
        null,
      );

      const thresholdTag =
        thresholdRawValue === null
          ? null
          : metric.thresholdType === "min"
            ? t("predictionStrings.minShort")
            : t("predictionStrings.maxShort");

      const thresholdValue =
        thresholdRawValue === null
          ? "-"
          : formatComparisonValue(thresholdRawValue);

      const rowValues = [
        formatComparisonValue(predictedMetric?.min),
        formatComparisonValue(predictedMetric?.max),
        formatComparisonValue(optimizedMetric?.min),
        formatComparisonValue(optimizedMetric?.max),
        thresholdValue === "-" ? "-" : `${thresholdValue} (${thresholdTag})`,
      ];

      rowValues.forEach((value, offset) => {
        const cell = row.getCell(startCol + offset);
        cell.value = value;
        Object.assign(cell, DATA_STYLE);
      });
    });

    row.commit();
  });

  sheet.getRow(1).height = 24;
  sheet.getRow(2).height = 24;
  sheet.getRow(3).height = 24;

  sheet.getColumn(1).width = 35;
  trainsData.forEach((_, trainIdx) => {
    const startCol = trainColMap[trainIdx];
    sheet.getColumn(startCol).width = 14;
    sheet.getColumn(startCol + 1).width = 14;
    sheet.getColumn(startCol + 2).width = 14;
    sheet.getColumn(startCol + 3).width = 14;
    sheet.getColumn(startCol + 4).width = 20;
  });

  addBordersToSheet(sheet);
};
